import { replaceEntities, updateEntities } from '../entities/slice';
import type { AppMiddleware } from '../types';

export const NEED_TO_GET_AUTH_URL_MARKER = 'NEED_TO_GET_AUTH';

type UrlEntity = { url?: string | null };

const isUsableUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' &&
  url.length > 0 &&
  !url.includes(NEED_TO_GET_AUTH_URL_MARKER);

const preserveSignedUrls = <Entity extends UrlEntity>(
  payloadEntities: Record<string, Partial<Entity>>,
  stateEntities: Record<string, Entity>,
): Record<string, Partial<Entity>> =>
  Object.fromEntries(
    Object.entries(payloadEntities).map(
      ([id, payloadEntity]): [string, Partial<Entity>] => {
        const existingUrl = stateEntities[id]?.url;
        if (isUsableUrl(existingUrl) && !isUsableUrl(payloadEntity.url)) {
          return [id, { ...payloadEntity, url: existingUrl }];
        }
        return [id, payloadEntity];
      },
    ),
  );

type KeyframesHolder = {
  keyframes?: (UrlEntity & { status?: string | null })[] | null;
};

const hasUsableKeyframe = (keyframes: KeyframesHolder['keyframes']) =>
  Boolean(keyframes?.some((keyframe) => isUsableUrl(keyframe.url)));

const preserveSearchItemKeyframes = <Item extends KeyframesHolder>(
  payloadItems: Record<string, Partial<Item>>,
  stateItems: Record<string, Item>,
): Record<string, Partial<Item>> =>
  Object.fromEntries(
    Object.entries(payloadItems).map(
      ([id, payloadItem]): [string, Partial<Item>] => {
        const { keyframes } = payloadItem;
        const existingKeyframes = stateItems[id]?.keyframes;
        if (
          keyframes?.length &&
          !hasUsableKeyframe(keyframes) &&
          !keyframes.some((keyframe) => keyframe.status === 'DELETED') &&
          hasUsableKeyframe(existingKeyframes)
        ) {
          return [id, { ...payloadItem, keyframes: existingKeyframes }];
        }
        return [id, payloadItem];
      },
    ),
  );

export const entityIgnoreNeedAuthUrlUpdateMiddleware: AppMiddleware =
  (api) => (next) => (action) => {
    if (!updateEntities.match(action) && !replaceEntities.match(action)) {
      return next(action);
    }

    const { files, proxies, keyframes, search_items } = action.payload;
    if (!files && !proxies && !keyframes && !search_items) {
      return next(action);
    }

    const {
      files: stateFiles,
      proxies: stateProxies,
      keyframes: stateKeyframes,
      search_items: stateSearchItems,
    } = api.getState().entities;
    return next({
      ...action,
      payload: {
        ...action.payload,
        ...(files ? { files: preserveSignedUrls(files, stateFiles) } : {}),
        ...(proxies
          ? { proxies: preserveSignedUrls(proxies, stateProxies) }
          : {}),
        ...(keyframes
          ? { keyframes: preserveSignedUrls(keyframes, stateKeyframes) }
          : {}),
        ...(search_items
          ? {
              search_items: preserveSearchItemKeyframes(
                search_items,
                stateSearchItems,
              ),
            }
          : {}),
      },
    });
  };
